import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";

function LabelValue({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className="mt-1 text-[15px] text-[#0A0A0A] break-words">{children ?? "—"}</div>
    </div>
  );
}

export default function HQOptimiseMonitoringDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setData(null);
    return hqApi.get(`/optimise/monitoring/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Something went wrong loading this profile."));
  };

  useEffect(() => {
    let alive = true;
    hqApi.get(`/optimise/monitoring/${id}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading this profile."));
    return () => { alive = false; };
  }, [id]);

  const patchStatus = async (status) => {
    setBusy(true);
    try {
      await hqApi.patch(`/optimise/monitoring/${id}`, { status });
      await load();
    } catch { /* no-op */ }
    finally { setBusy(false); }
  };

  const rescan = async () => {
    setBusy(true);
    try {
      await hqApi.post(`/optimise/monitoring/${id}/rescan`);
      await load();
    } catch { /* no-op */ }
    finally { setBusy(false); }
  };

  if (err) return <Card className="p-6" data-testid="hq-monitoring-detail-error">{err}</Card>;
  if (!data) return (
    <div className="space-y-4">
      <Skeleton h={40} w={220} />
      <Card className="p-6 space-y-3"><Skeleton h={20}/><Skeleton h={20}/><Skeleton h={20}/></Card>
    </div>
  );

  const { profile, report } = data;
  const website = report?.website;

  return (
    <div className="space-y-5" data-testid="hq-monitoring-detail">
      <Link to="/hq/optimise/monitoring" className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A] hover:text-[#0A0A0A]">
        <ArrowLeft size={14} /> All monitored websites
      </Link>

      <Card className="p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Monitoring profile</div>
            <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">
              {website?.normalized_domain || profile.website_id?.slice(0, 12)}
            </h1>
            <div className="mt-2 text-[13px] text-[#4A4A4A]">
              Status: {profile.status || "—"} · Cadence every {profile.frequency_hours || "?"}h
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={rescan} disabled={busy}
                    data-testid="hq-monitoring-detail-rescan"
                    className="px-4 py-2 rounded-full bg-black text-white text-[11px] font-black tracking-[0.2em] uppercase disabled:opacity-50">
              Rescan now
            </button>
            {profile.status === "active" ? (
              <button type="button" onClick={() => patchStatus("paused")} disabled={busy}
                      data-testid="hq-monitoring-detail-pause"
                      className="px-4 py-2 rounded-full border border-[#EAEAEA] bg-white text-[11px] font-black tracking-[0.2em] uppercase disabled:opacity-50">
                Pause
              </button>
            ) : (
              <button type="button" onClick={() => patchStatus("active")} disabled={busy}
                      data-testid="hq-monitoring-detail-resume"
                      className="px-4 py-2 rounded-full border border-[#EAEAEA] bg-white text-[11px] font-black tracking-[0.2em] uppercase disabled:opacity-50">
                Resume
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5">
          <LabelValue label="Current score">{profile.current_score ?? "—"}</LabelValue>
          <LabelValue label="Previous score">{profile.last_score ?? "—"}</LabelValue>
          <LabelValue label="Last scan">{profile.last_scan_at ? new Date(profile.last_scan_at).toLocaleString() : "—"}</LabelValue>
          <LabelValue label="Next scan">{profile.next_scan_at ? new Date(profile.next_scan_at).toLocaleString() : "—"}</LabelValue>
          <LabelValue label="Website">{website?.url || website?.normalized_domain || "—"}</LabelValue>
          <LabelValue label="Business">{profile.business_id || "—"}</LabelValue>
          <LabelValue label="Plan">{profile.plan_id || "—"}</LabelValue>
          <LabelValue label="Trend">{report?.trend?.overall || "unchanged"}</LabelValue>
        </div>
      </Card>

      <Card className="p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Scan history</div>
        <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">Recent scans</h2>
        <div className="mt-4">
          {(!report?.history || report.history.length === 0) ? (
            <EmptyState title="No scans yet" hint="Once the scheduler runs, historical scans will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">When</th>
                    <th className="text-left px-4 py-2 font-bold">Score</th>
                    <th className="text-left px-4 py-2 font-bold">Status</th>
                    <th className="text-left px-4 py-2 font-bold">Scan ID</th>
                  </tr>
                </thead>
                <tbody>
                  {report.history.map((h) => (
                    <tr key={h.id} className="border-t border-[#EAEAEA]" data-testid={`hq-monitoring-history-${h.id}`}>
                      <td className="px-4 py-2 text-[#8A8A8A]">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2 font-black">{h.score ?? "—"}</td>
                      <td className="px-4 py-2 text-[11px] tracking-[0.16em] uppercase text-[#8A8A8A]">{h.status}</td>
                      <td className="px-4 py-2 text-[12px] text-[#8A8A8A] break-all">{h.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
